import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import BuyerLandingHeader from "@/components/buyer/landing/BuyerLandingHeader";
import BuyerLandingFooter from "@/components/buyer/landing/BuyerLandingFooter";
import PageSEO from "@/components/seo/PageSEO";
import StructuredData from "@/components/seo/StructuredData";

// FAQ data organized by category
const buyerFaqs = {
  gettingStarted: [
    {
      question: "Is GoDyspo the same as GoDispo, Go Dyspo, or Go Dispo?",
      answer: "Yes — these are all common spellings of the same brand. The official name is GoDyspo and the only official website is https://godyspo.com. Any similarly named domain is not affiliated with us."
    },
    {
      question: "What is GoDyspo for buyers?",
      answer: "GoDyspo (also spelled GoDispo) is a real estate investment marketplace where you can discover wholesale deals, off-market properties, and investment opportunities directly from wholesalers and agents. Our platform helps you find, analyze, and secure properties faster than traditional methods."
    },
    {
      question: "How do I create a buyer account?",
      answer: "Click 'Create Free Account' on our buyer page, enter your investment preferences and contact information, verify your email, and start browsing properties immediately. Registration takes less than 5 minutes."
    },
    {
      question: "Is it really free?",
      answer: "Yes! Buyer accounts are completely free with full access to all buyer features. Browse properties, save favorites, follow sellers, and make offers without any commitment. No credit card required."
    },
    {
      question: "What's included with a buyer account?",
      answer: "Your free account includes unlimited property searches, saved searches with alerts, the ability to save favorite properties, follow preferred sellers, make offers on properties, and direct communication with sellers."
    }
  ],
  propertySearch: [
    {
      question: "How do I search for properties?",
      answer: "Use our powerful search tools to filter properties by location (city, state, or zip code), price range, property type, number of bedrooms and bathrooms, ARV, and more. Toggle between list and map views to find properties that match your investment criteria."
    },
    {
      question: "What types of properties are available?",
      answer: "GoDyspo features a variety of investment properties including single-family homes, multi-family properties, townhouses, condos, and land. Most listings are wholesale deals with attractive margins for investors."
    },
    {
      question: "Can I save my favorite properties?",
      answer: "Absolutely! Click the heart icon on any property to save it to your favorites. Access all your saved properties from the 'Saved Properties' section of your dashboard for quick reference and comparison."
    },
    {
      question: "How do saved searches work?",
      answer: "Create up to 8 saved searches with your preferred criteria. When new properties matching your search are listed, you'll receive automatic notifications via email and in-app alerts so you never miss a deal."
    }
  ],
  makingOffers: [
    {
      question: "How do I make an offer on a property?",
      answer: "On any property detail page, click the 'Make Offer' button. Enter your offer amount, proposed closing date, and any notes for the seller. Your offer is sent directly to the seller for review."
    },
    {
      question: "What information do I need to submit an offer?",
      answer: "You'll need to provide your offer price, desired closing date, and optionally any notes explaining your terms or financing situation. The more complete your offer, the better sellers can evaluate and respond."
    },
    {
      question: "How do I know if my offer was accepted?",
      answer: "You'll receive instant notifications when sellers respond to your offers. Check your Notifications tab or email for updates. The Offers section in your dashboard shows all your submitted offers and their current status."
    },
    {
      question: "Can I negotiate with sellers?",
      answer: "Yes! Sellers can accept, reject, or counter your offers. If a seller counters, you can review their terms and respond with another counter-offer. This process continues until both parties reach an agreement or decide to move on."
    }
  ],
  subscriptionPricing: [
    {
      question: "How much does it cost?",
      answer: "GoDyspo is completely free for buyers. You get unlimited access to search properties, save favorites, follow sellers, and submit offers on investment opportunities at no cost."
    },
    {
      question: "Can I delete my account anytime?",
      answer: "Yes, you can delete your free buyer account anytime with no fees or penalties, and you're welcome to create a new account whenever you're ready."
    },
    {
      question: "Do I need a credit card to sign up?",
      answer: "No. Buyer accounts are free and no credit card is required. Just register, verify your email, and start browsing properties immediately."
    },
    {
      question: "Will I ever be charged as a buyer?",
      answer: "No. Buyer access is free — there are no subscription fees, no trials that convert to paid plans, and no hidden charges."
    }
  ],
  dealCalculators: [
    {
      question: "What deal analysis tools are available?",
      answer: "GoDyspo provides two powerful deal analysis tools: the Repair Estimator for approximating rehab costs using editable industry-standard defaults, and the Profit Calculator for estimating net profit and return on investment across five cost categories."
    },
    {
      question: "How does the Repair Estimator work?",
      answer: "Enter your property's square footage, then expand each category (Exterior, Interior, Mechanicals, Other) to see individual line items. Adjust cost and quantity for any item to match your deal. A 22% contingency is automatically added for trim, hardware, and unexpected expenses."
    },
    {
      question: "How does the Profit Calculator work?",
      answer: "Enter your After Repair Value, purchase price, and estimated repairs. Then add holding costs, financing details, and transaction costs. The calculator shows your estimated Net Profit and ROI in real time, with a full cost breakdown."
    },
    {
      question: "Are the calculator results saved?",
      answer: "Both calculators are client-side tools and results are not saved between sessions. We recommend taking a screenshot or noting your numbers before navigating away from the page."
    }
  ],
  sellerInteraction: [
    {
      question: "How do I contact sellers?",
      answer: "Each property listing displays the seller's contact information. You can also submit offers directly through the platform, which notifies sellers and opens a communication channel for negotiations."
    },
    {
      question: "Can I follow favorite sellers?",
      answer: "Yes! When you find sellers whose deals match your criteria, click 'Follow' on their profile. You'll receive notifications when they list new properties, helping you get first access to their deals."
    },
    {
      question: "How do I get notified about new properties?",
      answer: "Set up saved searches to receive automatic alerts when new properties match your criteria. Follow your favorite sellers to be notified of their new listings. Manage notification preferences in your profile settings."
    }
  ]
};

// Flatten FAQs for structured data
const allFaqs = [
  ...buyerFaqs.gettingStarted,
  ...buyerFaqs.propertySearch,
  ...buyerFaqs.makingOffers,
  ...buyerFaqs.dealCalculators,
  ...buyerFaqs.subscriptionPricing,
  ...buyerFaqs.sellerInteraction
];

const BuyerFAQPage = () => {
  return (
    <div className="min-h-screen bg-[#080c14] flex flex-col">
      <PageSEO
        title="Buyer FAQ | Investment Property Questions"
        description="Find answers about searching for wholesale deals, making offers, subscription pricing, and connecting with sellers on GoDyspo's real estate investment platform."
        canonical="/buyer/faq"
        keywords="real estate investor FAQ, wholesale deal questions, property investment help, GoDyspo buyer support"
      />
      <StructuredData type="FAQPage" faqs={allFaqs} />
      
      <BuyerLandingHeader />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Buyer <span className="text-[#ffd700]">FAQ</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Get answers to common questions about finding investment properties and making offers on GoDyspo.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Getting Started */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Getting Started</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.gettingStarted.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`getting-started-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Property Search */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Property Search</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.propertySearch.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`search-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Making Offers */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Making Offers</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.makingOffers.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`offers-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Deal Calculators */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Deal Calculators</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.dealCalculators.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`calculators-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Subscription & Pricing */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Subscription & Pricing</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.subscriptionPricing.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`pricing-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Seller Interaction */}
          <section>
            <h2 className="text-2xl font-semibold text-[#ffd700] mb-4">Seller Interaction</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {buyerFaqs.sellerInteraction.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`seller-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-[#ffd700] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#ffd700]/10 to-[#0052cc]/10 rounded-2xl p-8 md:p-12 border border-gray-800">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Find Your Next Investment?
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Join thousands of investors discovering wholesale deals on GoDyspo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/buyer/register">
              <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] px-8 py-3 text-lg">
                Create Free Account
              </Button>
            </Link>
            <Link to="/buyer/pricing">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 text-lg">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 text-center">
          <p className="text-gray-400">
            Still have questions?{" "}
            <Link to="/contact" className="text-[#ffd700] hover:underline">
              Contact our support team
            </Link>
          </p>
        </div>
      </main>
      
      <BuyerLandingFooter />
    </div>
  );
};

export default BuyerFAQPage;
